// Service-worker update plumbing.
//
// vite-plugin-pwa emits an auto-updating Workbox SW (registerType: 'autoUpdate'
// in vite.config.js): when a new build is detected the SW skipWaiting +
// clientsClaim and the page reloads onto the new version with no prompt.
//
// The catch on installed Android PWAs: the browser only checks for a new sw.js
// on a cold navigation, but Android warm-resumes the webview without one — so a
// backgrounded PWA can keep running an old build indefinitely and never notice
// a deploy. We force the check ourselves: poll registration.update() on an
// interval and whenever the app returns to the foreground (the warm-resume
// path). Any new sw.js found this way is applied automatically by autoUpdate.
//
// `injectRegister` is set to false in vite.config.js so this is the single
// registration site — don't also let the plugin inject its own.

import { registerSW } from 'virtual:pwa-register';

const UPDATE_INTERVAL_MS = 60 * 60 * 1000; // 1 h

let registered = false;

// Call once on app boot. Idempotent.
export function setupServiceWorker(): void {
  if (registered || typeof window === 'undefined') return;
  registered = true;

  registerSW({
    immediate: true,
    onRegisteredSW(_swUrl, registration) {
      if (!registration) return;

      const check = () => {
        if (navigator.onLine) registration.update().catch(() => {});
      };

      // Long-lived installed PWAs: re-check on a timer.
      setInterval(check, UPDATE_INTERVAL_MS);

      // Warm resume: re-check the instant the app is brought to the foreground.
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') check();
      });
      window.addEventListener('focus', check);
    },
    onRegisterError(error) {
      // Non-fatal — the app still runs from the network without a SW.
      // eslint-disable-next-line no-console
      console.error('Service worker registration failed', error);
    },
  });
}
